import { useCallback, useState } from 'react';
import { FlowerGlyph } from './Icons';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface Bloom {
  id: string;
  offset: number;
  drift: number;
  rotation: number;
  scale: number;
  duration: number;
}

export function FlowerButton({
  disabled,
  total,
  onFlower,
  compact = false,
}: {
  disabled: boolean;
  total: number;
  onFlower(): void;
  compact?: boolean;
}) {
  const reducedMotion = useReducedMotion();
  const [blooms, setBlooms] = useState<Bloom[]>([]);
  const [pulse, setPulse] = useState(false);

  const send = useCallback(() => {
    onFlower();
    setPulse(false);
    requestAnimationFrame(() => setPulse(true));
    window.setTimeout(() => setPulse(false), 320);
    if (reducedMotion) return;

    const bloom = {
      id: crypto.randomUUID(),
      offset: Math.round(Math.random() * 52 - 26),
      drift: Math.round(Math.random() * 36 - 18),
      rotation: Math.round(Math.random() * 42 - 21),
      scale: Number((0.82 + Math.random() * 0.34).toFixed(2)),
      duration: Math.round(980 + Math.random() * 280),
    };
    setBlooms((current) => [...current.slice(-7), bloom]);
    window.setTimeout(
      () => setBlooms((current) => current.filter((item) => item.id !== bloom.id)),
      bloom.duration,
    );
  }, [onFlower, reducedMotion]);

  return (
    <div className="flower-control">
      <button
        className={`reaction-button flower ${compact ? 'is-compact' : ''} ${pulse ? 'is-pulsing' : ''}`}
        disabled={disabled}
        onClick={send}
        aria-label={`Send a flower. ${total} flowers total`}
      >
        <FlowerGlyph />
        <span>{compact ? 'Flower' : 'Send flower'}</span>
        <strong>{total.toLocaleString()}</strong>
      </button>
      <span
        className={`plus-one ${pulse ? 'is-visible' : ''}`}
        aria-hidden="true"
      >
        +1
      </span>
      <div className="flower-flight" aria-hidden="true">
        {blooms.map((bloom) => (
          <FlowerGlyph
            key={bloom.id}
            style={
              {
                '--flower-offset': `${bloom.offset}px`,
                '--flower-drift': `${bloom.drift}px`,
                '--flower-rotation': `${bloom.rotation}deg`,
                '--flower-scale': bloom.scale,
                '--flower-duration': `${bloom.duration}ms`,
              } as React.CSSProperties
            }
          />
        ))}
      </div>
    </div>
  );
}
